import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { formatDateTime } from "../../lib/cefr";

export function SpeakingScoreEditor({ attemptId, current, max, override, onChange }) {
  const auto = override?.auto_score ?? current;
  const [score, setScore] = useState(String(current ?? 0));
  const [reason, setReason] = useState(override?.reason || "");
  const [busy, setBusy] = useState(false);

  const run = async (req, okMsg) => {
    setBusy(true);
    try {
      const { data } = await req();
      onChange(data);
      setScore(String(data.section_scores.speaking));
      if (!data.speaking_override) setReason("");
      toast.success(okMsg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update the score");
    } finally {
      setBusy(false);
    }
  };

  const value = Number(score);
  const invalid = score === "" || Number.isNaN(value) || value < 0 || value > max;

  return (
    <div className="neu p-6" data-testid="speaking-score-editor">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-lg font-bold">Adjust speaking score</h3>
        <span className="text-xs text-muted-foreground" data-testid="speaking-score-auto">Automatic score: {auto} / {max}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Listen to the takes, then set the mark you think is fair. The total, level and B1 verdict update straight away.</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-sm font-bold">
          New score
          <div className="mt-1 flex items-center gap-2">
            <input
              type="number" min={0} max={max} step={0.5} value={score}
              onChange={(e) => setScore(e.target.value)}
              data-testid="speaking-score-input"
              className="w-24 border border-foreground/30 bg-transparent px-3 py-2 font-mono-fig focus:outline-none focus:border-foreground"
            />
            <span className="font-mono-fig text-muted-foreground">/ {max}</span>
          </div>
        </label>
      </div>
      <textarea
        value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={2}
        placeholder="Reason (optional, admins only)"
        data-testid="speaking-score-reason"
        className="mt-3 w-full border border-foreground/30 bg-transparent p-3 text-sm focus:outline-none focus:border-foreground"
      />
      {invalid && <p className="mt-1 text-xs text-red-600" data-testid="speaking-score-invalid">Enter a number from 0 to {max}.</p>}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground" data-testid="speaking-score-meta">
          {override ? `Adjusted ${formatDateTime(override.updated_at)} by ${override.updated_by}` : "Not adjusted"}
        </span>
        <div className="flex gap-2">
          {override && (
            <button
              onClick={() => run(() => api.delete(`/admin/attempts/${attemptId}/speaking-score`), "Restored the automatic score")}
              disabled={busy} data-testid="speaking-score-reset-btn"
              className="px-4 py-2 text-sm font-bold neu-btn disabled:opacity-50"
            >
              Use automatic score
            </button>
          )}
          <button
            onClick={() => run(() => api.put(`/admin/attempts/${attemptId}/speaking-score`, { score: value, reason }), "Speaking score updated")}
            disabled={busy || invalid} data-testid="speaking-score-save-btn"
            className="px-5 py-2 text-sm font-bold bg-foreground text-background border border-foreground hover:bg-signal hover:border-signal hover:text-ink disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save score"}
          </button>
        </div>
      </div>
    </div>
  );
}
